import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { getToken, setToken, getStoredUser, setStoredUser, logout as doLogout } from './api';

const AuthContext = createContext(null);

// Per-user session-storage keys (chat history, search filters, etc.) that
// MUST be wiped whenever the signed-in user identity changes — covers the
// case where a previous user closed the tab without logging out and a new
// user signs in on the same device.
function _wipePerUserStorage() {
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('qf-')) sessionStorage.removeItem(key);
        }
    } catch { /* noop */ }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(getStoredUser());
    const [org, setOrg] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!getToken()) {
            setUser(null);
            setOrg(null);
            setLoading(false);
            return;
        }
        try {
            const r = await api.get('/auth/me');
            setUser(r.data.user);
            setOrg(r.data.org);
            setStoredUser(r.data.user);
        } catch (_e) {
            setUser(null);
            setOrg(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const r = await api.post('/auth/login', { email, password });
        // Wipe any leftover per-user storage from a previous session BEFORE
        // setting the new user — otherwise the new user briefly sees the old
        // user's chat history / lead filters until they trigger their own writes.
        _wipePerUserStorage();
        setToken(r.data.access_token);
        setStoredUser(r.data.user);
        setUser(r.data.user);
        await refresh();
        return r.data.user;
    };

    const signup = async (payload) => {
        const r = await api.post('/auth/signup', payload);
        _wipePerUserStorage();
        setToken(r.data.access_token);
        setStoredUser(r.data.user);
        setUser(r.data.user);
        await refresh();
        return r.data.user;
    };

    const logout = () => {
        doLogout();
        setUser(null);
        setOrg(null);
    };

    return (
        <AuthContext.Provider value={{ user, org, loading, login, signup, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
